import { useEffect, useRef, useState, type RefObject } from "react";
import { authFetchJson } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { eventsQuery, filterChips, mergeFilters, messageKey, messagesQuery, withoutFilter } from "./explorerQuery";
import {
  CLASS_LABEL, EXPLORER_KIND_LABEL, SURFACE_LABEL,
  type ExplorerFilters, type ExplorerKind, type TelemetryEvent, type TelemetryEventsPage, type TelemetryMessage,
} from "./types";
import { Card } from "./ui";

type Mode = "list" | "grouped";

const KINDS: ExplorerKind[] = ["error", "upload_failed", "upload_queued", "upload_recovered"];
const MODES: Mode[] = ["list", "grouped"];

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

function isExplorerKind(value: string): value is ExplorerKind {
  return KINDS.some((k) => k === value);
}

function EventRow({ e, onUserClick }: { e: TelemetryEvent; onUserClick: (userId: string, email: string) => void }) {
  const status = e.metadata.status;
  const permanent = e.metadata.permanent === true;
  return (
    <li className="border-t border-[rgba(10,52,96,0.06)] py-2 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="tabular-nums text-xs text-[var(--cs-ink-muted)]">{formatDateTime(e.created_at)}</span>
        <button
          type="button"
          onClick={() => { onUserClick(e.user_id, e.email); }}
          className="text-[var(--cs-navy)] underline-offset-2 hover:underline"
        >
          {e.email || e.user_id.slice(0, 8)}
        </button>
        {e.organization ? <span className="text-xs text-[var(--cs-ink-muted)]">{e.organization}</span> : null}
        <span className="text-xs text-[var(--cs-ink-muted)]">
          {SURFACE_LABEL[e.surface]}{e.version ? ` ${e.version}` : ""}{e.platform ? ` · ${e.platform}` : ""}
        </span>
        {e.screen ? <span className="text-xs text-[var(--cs-ink-muted)]">{e.screen}</span> : null}
      </div>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
        <span className="text-xs text-[var(--cs-rose)]">
          {CLASS_LABEL[e.error_class ?? "other"] ?? e.error_class} · {e.source ?? "app"}
        </span>
        {e.detail ? <span className="whitespace-pre-wrap break-words text-[var(--cs-navy)]">{e.detail}</span> : null}
        {typeof status === "number" ? <span className="text-xs text-[var(--cs-ink-muted)]">HTTP {status}</span> : null}
        {e.attempt !== null ? <span className="text-xs text-[var(--cs-ink-muted)]">poging {e.attempt}</span> : null}
        {permanent ? <span className="text-xs text-[var(--cs-ink-muted)]">definitief</span> : null}
      </div>
    </li>
  );
}

function useEventsPage(path: string): {
  events: TelemetryEvent[]; nextBeforeId: number | null; loading: boolean; error: string | null;
  paginationError: string | null; loadMore: (path: string) => void;
} {
  const [events, setEvents] = useState<TelemetryEvent[]>([]);
  const [nextBeforeId, setNextBeforeId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paginationError, setPaginationError] = useState<string | null>(null);
  const currentPath = useRef(path);

  useEffect(() => {
    currentPath.current = path;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPaginationError(null);
    setEvents([]);
    setNextBeforeId(null);
    authFetchJson<TelemetryEventsPage>(path)
      .then((p) => { if (!cancelled) { setEvents(p.events); setNextBeforeId(p.next_before_id); } })
      .catch((e: unknown) => { if (!cancelled) setError(errorText(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path]);

  const loadMore = (morePath: string) => {
    const requestedFor = currentPath.current;
    setLoading(true);
    setPaginationError(null);
    authFetchJson<TelemetryEventsPage>(morePath)
      .then((p) => {
        if (currentPath.current !== requestedFor) return;
        setEvents((prev) => [...prev, ...p.events]);
        setNextBeforeId(p.next_before_id);
      })
      .catch((e: unknown) => { if (currentPath.current === requestedFor) setPaginationError(errorText(e)); })
      .finally(() => { if (currentPath.current === requestedFor) setLoading(false); });
  };

  return { events, nextBeforeId, loading, error, paginationError, loadMore };
}

function EventList({
  filters, days, onUserClick,
}: { filters: ExplorerFilters; days: number; onUserClick: (userId: string, email: string) => void }) {
  const page = useEventsPage(eventsQuery(filters, days));
  if (page.error) return <p className="text-sm text-[var(--cs-rose)]">Telemetrie tijdelijk niet beschikbaar: {page.error}</p>;
  if (page.events.length === 0) return <p className="text-sm text-[var(--cs-ink-muted)]">{page.loading ? "Laden…" : "Geen"}</p>;
  return (
    <div>
      <ul>
        {page.events.map((e) => <EventRow key={e.id} e={e} onUserClick={onUserClick} />)}
      </ul>
      {page.paginationError ? (
        <p className="mt-2 text-xs text-[var(--cs-rose)]">Telemetrie tijdelijk niet beschikbaar: {page.paginationError}</p>
      ) : null}
      {page.nextBeforeId !== null ? (
        <button
          type="button"
          disabled={page.loading}
          onClick={() => { page.loadMore(eventsQuery(filters, days, page.nextBeforeId)); }}
          className="mt-3 rounded-full border border-[rgba(10,52,96,0.10)] px-3 py-1 text-xs text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)] disabled:opacity-50"
        >
          {page.loading ? "Laden…" : "Meer laden"}
        </button>
      ) : null}
    </div>
  );
}

function GroupedList({
  filters, days, onUserClick, onShowAsList,
}: {
  filters: ExplorerFilters; days: number;
  onUserClick: (userId: string, email: string) => void;
  onShowAsList: (f: ExplorerFilters) => void;
}) {
  const [messages, setMessages] = useState<TelemetryMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const path = messagesQuery(filters, days);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setExpanded(null);
    authFetchJson<{ messages: TelemetryMessage[] }>(path)
      .then((r) => { if (!cancelled) setMessages(r.messages); })
      .catch((e: unknown) => { if (!cancelled) setError(errorText(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path]);

  if (error) return <p className="text-sm text-[var(--cs-rose)]">Telemetrie tijdelijk niet beschikbaar: {error}</p>;
  if (messages.length === 0) return <p className="text-sm text-[var(--cs-ink-muted)]">{loading ? "Laden…" : "Geen"}</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs uppercase tracking-wide text-[var(--cs-ink-muted)]">
          <th className="py-1">Klasse</th>
          <th className="py-1">Bron</th>
          <th className="py-1">Melding</th>
          <th className="py-1 text-right">Aantal</th>
          <th className="py-1 text-right">Gebruikers</th>
          <th className="py-1">Apps</th>
          <th className="py-1">Laatst</th>
        </tr>
      </thead>
      <tbody>
        {messages.map((m) => {
          const key = messageKey(m);
          const open = expanded === key;
          const groupFilters = mergeFilters(filters, { error_class: m.error_class, source: m.source, detail: m.detail ?? "" });
          return (
            <GroupRow
              key={key}
              m={m}
              open={open}
              onToggle={() => { setExpanded(open ? null : key); }}
              groupFilters={groupFilters}
              days={days}
              onUserClick={onUserClick}
              onShowAsList={onShowAsList}
            />
          );
        })}
      </tbody>
    </table>
  );
}

function GroupRow({
  m, open, onToggle, groupFilters, days, onUserClick, onShowAsList,
}: {
  m: TelemetryMessage; open: boolean; onToggle: () => void; groupFilters: ExplorerFilters; days: number;
  onUserClick: (userId: string, email: string) => void; onShowAsList: (f: ExplorerFilters) => void;
}) {
  return (
    <>
      <tr onClick={onToggle} className="cursor-pointer border-t border-[rgba(10,52,96,0.06)] hover:bg-[var(--cs-ivory)]">
        <td className="py-2 text-[var(--cs-rose)]">{CLASS_LABEL[m.error_class] ?? m.error_class}</td>
        <td className="py-2">{m.source}</td>
        <td className="py-2 whitespace-pre-wrap break-words text-[var(--cs-navy)]">{m.detail ?? "—"}</td>
        <td className="py-2 text-right tabular-nums">{m.count}</td>
        <td className="py-2 text-right tabular-nums">{m.users}</td>
        <td className="py-2 text-xs text-[var(--cs-ink-muted)]">{m.surfaces.map((s) => SURFACE_LABEL[s]).join(", ")}</td>
        <td className="py-2 text-xs text-[var(--cs-ink-muted)]">{formatDateTime(m.last_seen)}</td>
      </tr>
      {open ? (
        <tr className="border-t border-[rgba(10,52,96,0.06)] bg-[var(--cs-ivory)]">
          <td colSpan={7} className="p-3">
            <EventList filters={groupFilters} days={days} onUserClick={onUserClick} />
            <button
              type="button"
              onClick={() => { onShowAsList(groupFilters); }}
              className="mt-2 text-xs text-[var(--cs-navy)] underline-offset-2 hover:underline"
            >
              Toon als lijst
            </button>
          </td>
        </tr>
      ) : null}
    </>
  );
}

export function ProblemsExplorer({
  filters, onFiltersChange, days, onUserClick, anchorRef,
}: {
  filters: ExplorerFilters;
  onFiltersChange: (f: ExplorerFilters) => void;
  days: number;
  onUserClick: (userId: string, email: string) => void;
  anchorRef: RefObject<HTMLDivElement | null>;
}) {
  const [mode, setMode] = useState<Mode>("list");
  const chips = filterChips(filters);

  return (
    <div ref={anchorRef}>
      <Card caption="Foutenverkenner">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <select
            value={filters.kind}
            onChange={(ev) => { if (isExplorerKind(ev.target.value)) onFiltersChange({ ...filters, kind: ev.target.value }); }}
            className="rounded-full border border-[rgba(10,52,96,0.10)] bg-white px-3 py-1 text-xs text-[var(--cs-navy)]"
          >
            {KINDS.map((k) => <option key={k} value={k}>{EXPLORER_KIND_LABEL[k]}</option>)}
          </select>
          <div className="inline-flex rounded-full border border-[rgba(10,52,96,0.10)] bg-white p-1">
            {MODES.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  if (m === "grouped" && filters.detail !== undefined) onFiltersChange(withoutFilter(filters, "detail"));
                  setMode(m);
                }}
                className={`rounded-full px-3 py-1 text-xs transition-colors ${
                  mode === m ? "bg-[rgba(225,29,72,0.06)] text-[var(--cs-navy)]" : "text-[var(--cs-ink-muted)] hover:text-[var(--cs-navy)]"
                }`}
              >
                {m === "list" ? "Lijst" : "Per melding"}
              </button>
            ))}
          </div>
          {chips.map((c) => (
            <span key={c.key} className="inline-flex items-center gap-1 rounded-full bg-[rgba(10,52,96,0.06)] px-3 py-1 text-xs text-[var(--cs-navy)]">
              {c.label}
              <button type="button" aria-label={`Verwijder filter ${c.label}`} onClick={() => { onFiltersChange(withoutFilter(filters, c.key)); }}>×</button>
            </span>
          ))}
          {chips.length > 0 ? (
            <button
              type="button"
              onClick={() => { onFiltersChange({ kind: filters.kind }); }}
              className="text-xs text-[var(--cs-ink-muted)] hover:text-[var(--cs-navy)]"
            >
              Wis filters
            </button>
          ) : null}
        </div>
        {mode === "list" ? (
          <EventList filters={filters} days={days} onUserClick={onUserClick} />
        ) : (
          <GroupedList
            filters={filters}
            days={days}
            onUserClick={onUserClick}
            onShowAsList={(f) => { onFiltersChange(f); setMode("list"); }}
          />
        )}
      </Card>
    </div>
  );
}
